import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { createLesson } from '@/data/services/lessonServices';
import { updateLessonBlock } from '@/data/services/lessonBlocksServices';
import useUnitWorkspace from './useUnitWorkspace';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';

type UseUnitContentLessonParams = {
  enabled: boolean;
};

export const useUnitContentLesson = ({
  enabled,
}: UseUnitContentLessonParams) => {
  const { unit, slug, cache } = useUnitWorkspace();

  const { mutate, status, isPending, reset } = useMutation({
    mutationFn: async () => {
      if (!slug || !unit || unit.contentLesson) return;

      const lesson = await createLesson({ name: unit.name });
      await updateLessonBlock(slug, unit.id, { contentLessonId: lesson.id });
      cache.merge({
        contentLesson: {
          id: lesson.id,
          name: lesson.name,
          book: lesson.book,
          hasResources: lesson.resources.length > 0,
        },
      });
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  useEffect(() => {
    reset();
  }, [unit?.id, reset]);

  useEffect(() => {
    if (enabled && unit && !unit.contentLesson && status === 'idle') {
      mutate();
    }
  }, [enabled, unit, status, mutate]);

  return {
    contentLessonId: unit?.contentLesson?.id,
    isPending: isPending,
    isError: status === 'error',
    retry: mutate,
  };
};
